
import React from "react";
import { CheckCircle, MessageCircle, Target, Users } from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";

const Mentoria: React.FC = () => {
  useScrollReveal();

  const benefits = [
    "Sessões semanais de mentoria individual (4 encontros)",
    "Acompanhamento direto por WhatsApp",
    "Acesso aos meus frameworks e materiais internos",
    "Visão externa e estratégica sobre o que tu tá construindo",
    "Alguém no teu lado puxando tua régua pra cima"
  ];

  const targetAudience = [
    "Criadores que querem crescer com consistência",
    "Profissionais que sentem que têm potencial, mas tão travados",
    "Gente que quer montar um projeto do zero (marca pessoal, comunidade, conteúdo ou infoproduto)",
    "Quem já tá no jogo, mas quer mais foco, mais resultado e mais clareza"
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Hero Section with Image and Gradient */}
      <section className="relative py-20 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/lovable-uploads/3c0028c8-05f2-47a9-98de-5a6702082529.png"
            alt="Dudu Crespo"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/60" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="scroll-reveal">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
              🚀 Mentoria Individual<br />
              <span className="text-white/90">com Dudu Crespo</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-white/90">
              Direto ao ponto. Sem enrolação. Só o que faz tu sair do lugar.
            </p>
            <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed mb-6">
              Se tu sente que tá andando em círculos, procrastinando o que mais importa ou sem clareza de onde colocar tua energia… essa mentoria foi feita pra ti.
            </p>
            <p className="text-lg text-white/80 font-medium">
              Aqui, a gente não vai filosofar — a gente vai resolver.
            </p>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            🧠 Como funciona?
          </h2>
          <div className="bg-white/5 rounded-lg p-8 border border-white/10">
            <p className="text-lg text-white/80 mb-4">
              Mentoria individual 1:1 comigo, totalmente personalizada.
            </p>
            <p className="text-lg text-white/80 mb-6">
              A gente se encontra 1x por semana (durante 1 mês) e traça um plano real, com metas claras e acompanhamento direto.
            </p>
            <div className="text-center">
              <p className="text-lg font-medium text-white/90 mb-2">
                Não é fórmula mágica.
              </p>
              <p className="text-lg font-medium text-white/90">
                É estratégia, execução e responsabilidade.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            📌 Pra quem é:
          </h2>
          <div className="grid gap-4">
            {targetAudience.map((item, index) => (
              <div
                key={index}
                className="scroll-reveal flex items-start gap-4 bg-white/5 rounded-lg p-6 border border-white/10"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Target className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                <p className="text-lg text-white/80">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            ✅ O que tu vai levar:
          </h2>
          <div className="grid gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="scroll-reveal flex items-start gap-4 bg-green-500/10 rounded-lg p-6 border border-green-400/20"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                <p className="text-lg text-white/80">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            📷 Quem vai caminhar contigo?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <img
                src="/lovable-uploads/305db100-b812-4ced-b5c8-56a0e094d2e5.png"
                alt="Dudu Crespo correndo"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <p className="text-lg text-white/80 leading-relaxed">
                Eu sou o Dudu, e minha missão é simples: te ajudar a sair da inércia e assumir o controle da tua vida.
              </p>
              <p className="text-lg text-white/80 leading-relaxed">
                Não sou coach motivacional. Sou alguém que vive o que ensina, que passou pelos mesmos travamentos que tu talvez esteja passando, e que encontrou um caminho claro pra sair disso.
              </p>
              <p className="text-lg text-white/80 leading-relaxed">
                Minha abordagem é direta, sem enrolação, focada em resultados reais que tu consegue ver e sentir na tua vida.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            💬 Depoimentos
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <p className="text-white/80 italic mb-4">
                "O conteúdo do Dudu é diferente de tudo que já vi. Direto, sem enrolação, e que realmente funciona na prática."
              </p>
              <p className="text-white/60 text-sm">- Seguidor do Instagram</p>
            </div>
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <p className="text-white/80 italic mb-4">
                "Finalmente alguém que fala sobre produtividade de forma real, sem frescura. Mudou minha perspectiva."
              </p>
              <p className="text-white/60 text-sm">- Seguidor do YouTube</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-6 md:px-10 max-w-4xl mx-auto">
        <div className="scroll-reveal">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            💸 Investimento:
          </h2>
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-lg p-8 border border-blue-400/30 text-center">
            <div className="mb-6">
              <p className="text-3xl md:text-4xl font-bold text-white mb-2">
                R$497 à vista
              </p>
              <p className="text-xl text-white/80">
                ou 2x de R$260
              </p>
            </div>
            <div className="mb-8">
              <p className="text-lg text-yellow-300 font-medium mb-2">
                Vagas limitadas.
              </p>
              <p className="text-white/70">
                Só abro pra poucos por mês — se tiver vaga, o botão vai funcionar.
              </p>
            </div>
            <button className="bg-white text-black font-bold text-lg px-8 py-4 rounded-full hover:bg-white/90 transition-all transform hover:scale-105">
              👉 GARANTIR MINHA VAGA
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-sm opacity-50 border-t border-white/10">
        <p>© {new Date().getFullYear()} ESCAPE CLUB. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default Mentoria;
